import { api } from './api';
import { LoginCredentials, AuthResponse, User, AuthUser, Profile } from '../types';

export type { AuthUser };

export const login = async (credentials: LoginCredentials): Promise<AuthResponse> => {
    try {
        const response = await api.post<{ token: string; user: User }>('/auth/login', credentials);
        api.setToken(response.token);
        return {
            user: response.user,
            session: { access_token: response.token } as any,
            error: null
        };
    } catch (error: any) {
        console.error('Login error:', error);
        return {
            user: null,
            session: null,
            error: error.message || 'Login failed'
        };
    }
};

export const signIn = async (email: string, password: string): Promise<{ user: AuthUser | null; error: string | null }> => {
    const res = await login({ email, password });
    if (res.user) {
        return { user: { ...res.user, session: res.session }, error: null };
    }
    return { user: null, error: res.error || 'Failed' };
};

export const logout = async (): Promise<void> => {
    api.clearToken();
    window.location.reload();
};

export const signOut = logout;

export const getCurrentUser = async (): Promise<User | null> => {
    const token = localStorage.getItem('auth_token');
    if (!token) return null;
    try {
        const response = await api.get<{ user: User }>('/auth/me');
        return response.user;
    } catch (error) {
        return null;
    }
};

export const getCurrentSession = async (): Promise<AuthUser | null> => {
    const user = await getCurrentUser();
    if (user) return { ...user, session: { access_token: 'mock' } };
    return null;
};

export const getStaffMembers = async (): Promise<Profile[]> => {
    try {
        return await api.get<Profile[]>('/auth/profiles');
    } catch (error) {
        console.error('Error fetching staff members:', error);
        return [];
    }
};

export const createStaffMember = async (data: Partial<Profile> & { password?: string }): Promise<{ profile?: Profile; error?: string }> => {
    try {
        const res = await api.post<Profile>('/auth/profiles', data);
        return { profile: res };
    } catch (error: any) {
        console.error('Error creating staff member:', error);
        return { error: error.message || 'Failed to create staff member' };
    }
};

export const updateUserRole = async (userId: string, role: string): Promise<Profile | null> => {
    try {
        return await api.patch<Profile>(`/auth/profiles/${userId}/role`, { role });
    } catch (error) {
        console.error('Error updating user role:', error);
        return null;
    }
};

export const updateProfile = async (userId: string, updates: Partial<Profile>): Promise<Profile | null> => {
    try {
        return await api.patch<Profile>(`/auth/profiles/${userId}`, updates);
    } catch (error) {
        console.error('Error updating profile:', error);
        return null;
    }
};

export const updateStaffPassword = async (userId: string, newPassword: string): Promise<{ success: boolean; error?: string }> => {
    try {
        await api.patch(`/auth/profiles/${userId}/password`, { password: newPassword });
        return { success: true };
    } catch (error: any) {
        console.error('Error updating staff password:', error);
        return { success: false, error: error.message || 'Failed to update password' };
    }
};

export const deleteProfile = async (userId: string): Promise<{ success: boolean; error?: string }> => {
    try {
        await api.delete(`/auth/profiles/${userId}`);
        return { success: true };
    } catch (error: any) {
        console.error('Error removing staff:', error);
        return { success: false, error: error.message || 'Failed to remove staff' };
    }
};

export const onAuthStateChange = (callback: (user: AuthUser | null) => void) => {
    // Mock subscription
    getCurrentSession().then(callback);
    return {
        data: {
            subscription: {
                unsubscribe: () => { }
            }
        }
    };
};
